import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { ListState } from '@/hooks/useAppData';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { LIST_PAGE_SIZE, lastListPage, parseListPage } from '@/seo/urls';

/**
 * A crawlable, paged catalog list (web plan W0.11), shared by /brands,
 * /promotions and /shops.
 *
 * The URL's `?page=N` decides what the page is: it renders page N's items
 * (LIST_PAGE_SIZE of them) and the page renders `<Pagination>` links, real
 * `<a href>`s, to the other pages, so a crawler that never scrolls still
 * reaches every item.
 *
 * Infinite scroll is OFF by default (CATALOG_INFINITE_SCROLL): page N shows
 * page N's items and nothing else, as the owner expects (2026-10-03 bug
 * report: "every page shows all 82 brands"). With it on, a 24-item page is
 * barely taller than a desktop screen, so the sentinel sits inside the 600px
 * trigger margin and the first wheel tick appended page after page until the
 * end of the list, while the address bar moved to the last page loaded:
 * /brands showed every brand under `/brands?page=4`. The code below stays for
 * `{ infiniteScroll: true }`.
 *
 * With infinite scroll on, nearing the end of the list loads the
 * next page (useInfiniteScroll), and the address bar follows with
 * `history.replaceState`: no router navigation, so the items, the title and the
 * canonical stay those of the page that was opened, and a reload or Back lands
 * on the last page loaded. Opening another page (a pagination link, Back,
 * Forward) starts the list over at that page.
 *
 * Infinite scroll starts only once a person is there (useVisitorActive): a
 * scroll, wheel, touch or key, or an interaction earlier in this document.
 * Google renders with a very tall viewport and never scrolls, so the sentinel
 * would otherwise be "in view" on load and the rendered page N would also hold
 * pages N+1…, the same items on several self-canonical URLs. The HTML is the
 * same for everyone; a screen taller than the list has the Next link.
 *
 * A `?page=` that is not a whole number from 1 to MAX_LIST_PAGE is `invalid`
 * (and fetches page 1, for the count); a page after the last one is
 * `past_end`. Both render "No page N — go to page 1" and are `noindex`, while
 * loading and errors never change robots (the W1 server answers 404).
 */

export type ListPageStatus = 'ok' | 'invalid' | 'past_end';

export interface PaginatedList<T> {
  status: ListPageStatus;
  /** The page the URL names: the first page on screen (1 for an invalid value). */
  page: number;
  /** The last page on screen: `page` plus the pages infinite scroll appended. */
  throughPage: number;
  /** The page as the URL wrote it, for "No page N" (`999`, `abc`). */
  requested: string;
  /** Items of pages `page` … `throughPage`, in order, each once. */
  items: T[];
  /** The list's size once the backend has reported it (kept across pages). */
  totalRecords: number | null;
  /** The list's last page, from `totalRecords`. */
  lastPage: number | null;
  /** Nothing on screen yet for this page (skeleton). */
  initialLoading: boolean;
  /** Another page is on its way below the items (spinner). */
  loadingMore: boolean;
  error: string | null;
  refetch: () => void;
  hasMore: boolean;
  /** Ref for the infinite-scroll sentinel after the last item. */
  sentinelRef: (node: HTMLElement | null) => void;
  /** For `<PageHead/>`: the page (" – Page N", `?page=N`) and `noindex` for a page that does not exist. */
  seo: { page: number | undefined; noindex: boolean };
}

/** One run of pages, from the page the URL opened. */
interface Run<T> {
  start: number;
  /** The page being asked for: `start`, then each page infinite scroll adds. */
  page: number;
  items: T[];
  /** The last page whose answer is in `items` (`start - 1` before the first). */
  loadedThrough: number;
}

const runFrom = <T,>(start: number): Run<T> => ({
  start,
  page: start,
  items: [],
  loadedThrough: start - 1,
});

const sameItems = <T,>(a: readonly T[], b: readonly T[]) =>
  a === b || (a.length === b.length && a.every((item, i) => item === b[i]));

/** Input only a person produces; each one counts even when nothing scrolls. */
const PERSON_INPUT_EVENTS = ['wheel', 'touchmove', 'keydown'] as const;

/**
 * True once a person is using the page: it scrolled (`scrollY > 0`; a scroll
 * event that leaves the page at the top does not count), or a wheel, touch or
 * key came in, or the document had already been interacted with when the list
 * mounted (`navigator.userActivation`, e.g. the click that opened this list, so
 * moving between the catalog pages keeps infinite scroll on a tall screen).
 * A renderer that loads the URL and reads the DOM does none of these.
 */
function useVisitorActive(): boolean {
  const [active, setActive] = useState(false);
  useEffect(() => {
    if (active) return;
    if (window.scrollY > 0 || navigator.userActivation?.hasBeenActive === true) {
      setActive(true);
      return;
    }
    const onScroll = () => {
      if (window.scrollY > 0) setActive(true);
    };
    const onInput = () => setActive(true);
    window.addEventListener('scroll', onScroll, { passive: true });
    for (const type of PERSON_INPUT_EVENTS) {
      window.addEventListener(type, onInput, { passive: true });
    }
    return () => {
      window.removeEventListener('scroll', onScroll);
      for (const type of PERSON_INPUT_EVENTS) window.removeEventListener(type, onInput);
    };
  }, [active]);
  return active;
}

/** Whether the catalog lists append the next pages as a person scrolls (see the file comment). */
export const CATALOG_INFINITE_SCROLL = false;

export interface PaginatedListOptions {
  /** Append the next pages near the end of the list. Default CATALOG_INFINITE_SCROLL. */
  infiniteScroll?: boolean;
}

/**
 * @param useList one page of the list, e.g. `useBrands`. Always pass the same
 *   hook: it is called like any other hook.
 */
export function usePaginatedList<T extends { id: string }>(
  useList: (page: number, limit: number) => ListState<T>,
  { infiniteScroll = CATALOG_INFINITE_SCROLL }: PaginatedListOptions = {},
): PaginatedList<T> {
  const { pathname, search, hash } = useLocation();
  const param = parseListPage(search);
  const start = param.page;

  const [storedRun, setRun] = useState<Run<T>>(() => runFrom<T>(start));
  let run = storedRun;
  if (storedRun.start !== start) {
    // Another page was opened: start over there (React's "adjust state when a
    // prop changes" pattern, so no render shows the old page's items).
    run = runFrom<T>(start);
    setRun(run);
  }

  const { items: pageItems, loading, error, pagination, refetch } = useList(
    run.page,
    LIST_PAGE_SIZE,
  );
  // The opened page's answer is on screen in the render that receives it, as
  // the effect below will store it: a server-rendered list (web plan W4.5, the
  // route's copy, @/hooks/routePreload) has its items in the HTML, and the
  // render that hydrates it shows the same ones (effects never run on the
  // server). Infinite scroll still waits for the stored run (`hasMore`): its
  // next page is counted from what the state holds.
  const stored = run;
  if (run.loadedThrough < run.start && run.page === run.start && pagination) {
    run = { ...run, items: pageItems, loadedThrough: run.start };
  }

  // Add each answer to the run. Only the page being asked for answers here:
  // the first page of the run replaces the items (so a refetch or a cache
  // update shows through), later pages append what is new.
  useEffect(() => {
    // No answer for this page yet (in flight, or failed): nothing to add.
    if (!pagination) return;
    const answered = run.page;
    setRun((prev) => {
      if (prev.start !== start || prev.page !== answered) return prev;
      if (answered === prev.start) {
        if (prev.loadedThrough === answered && sameItems(prev.items, pageItems)) return prev;
        return { ...prev, items: pageItems, loadedThrough: answered };
      }
      const seen = new Set(prev.items.map((item) => item.id));
      const fresh = pageItems.filter((item) => !seen.has(item.id));
      if (fresh.length === 0 && prev.loadedThrough >= answered) return prev;
      return {
        ...prev,
        items: fresh.length > 0 ? [...prev.items, ...fresh] : prev.items,
        loadedThrough: Math.max(prev.loadedThrough, answered),
      };
    });
  }, [pagination, pageItems, run.page, start]);

  // The list's size, kept while the next page is in flight (a page change
  // must not flicker the count or the pagination away).
  const [storedTotal, setTotalRecords] = useState<number | null>(null);
  useEffect(() => {
    if (pagination) setTotalRecords(pagination.totalRecords);
  }, [pagination]);
  // Before the effect stores it (the server render, the render that hydrates
  // it), the answer at hand.
  const totalRecords = storedTotal ?? pagination?.totalRecords ?? null;

  const lastPage = totalRecords === null ? null : lastListPage(totalRecords);

  // The page the URL opened has been answered, and it does not exist.
  const startAnswered = run.page === run.start && pagination !== null;
  const pastEnd =
    param.valid &&
    startAnswered &&
    start > 1 &&
    (pageItems.length === 0 || start > lastListPage(pagination.totalRecords));
  const status: ListPageStatus = !param.valid ? 'invalid' : pastEnd ? 'past_end' : 'ok';

  const onScreen = run.loadedThrough >= run.start;
  // Off: never a next page here (no sentinel, no append, no replaceState);
  // the Next / numbered links open the other pages.
  const hasMore =
    infiniteScroll &&
    status === 'ok' &&
    stored.loadedThrough >= stored.start &&
    lastPage !== null &&
    run.loadedThrough < lastPage;

  // The sentinel still renders (`hasMore` is returned as is); it only loads
  // once a person is there, so a tall render of page N is page N's items.
  const visitorActive = useVisitorActive();

  const sentinelRef = useInfiniteScroll({
    hasMore: hasMore && visitorActive,
    // `error` blocks auto-loading: without it a failing request would retry on
    // every intersection, hammering the server while the user sits at the
    // bottom. The page's Retry link is the way back.
    loading: loading || Boolean(error),
    // The next page after the last one ANSWERED, read from the same state
    // update that added its items, so a missed or repeated trigger can never
    // skip a page or ask for the one already on screen.
    onLoadMore: () =>
      setRun((prev) => {
        if (prev.start !== start) return prev;
        const next = prev.loadedThrough + 1;
        return next > prev.page ? { ...prev, page: next } : prev;
      }),
  });

  // Keep the address bar on the last page loaded (history.replaceState, not a
  // router navigation, which would restart the list). The router's own state
  // object is passed back so Back/Forward keep working.
  const loadedThrough = run.loadedThrough;
  useEffect(() => {
    if (loadedThrough <= start) return;
    const params = new URLSearchParams(search);
    params.set('page', String(loadedThrough));
    try {
      window.history.replaceState(window.history.state, '', `${pathname}?${params}${hash}`);
    } catch {
      // A browser that refuses (sandboxed frame, rate limit) keeps the old URL.
    }
  }, [loadedThrough, start, pathname, search, hash]);

  // A new page opened from further down (the pagination sits under the list):
  // show it from the top. Not on the first render — ScrollToTop owns arrival.
  const openedRef = useRef(false);
  useEffect(() => {
    if (!openedRef.current) {
      openedRef.current = true;
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [start]);

  return {
    status,
    page: start,
    throughPage: Math.max(run.loadedThrough, start),
    requested: param.valid ? String(start) : (param.raw ?? ''),
    items: status === 'ok' ? run.items : [],
    totalRecords,
    lastPage,
    initialLoading: status === 'ok' && !onScreen && !error,
    loadingMore: loading && onScreen,
    error,
    refetch,
    hasMore,
    sentinelRef,
    seo: { page: param.valid ? start : undefined, noindex: status !== 'ok' },
  };
}

export default usePaginatedList;
